import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, exhaustMap, filter, map, mergeMap, of } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import { TemplateService } from '@ps/survey-template-data-access';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';

@Injectable()
export class SurveyCreationTemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _templateService = inject(TemplateService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  public readonly createSurveyFromTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.createSurveyFromTemplate),
      exhaustMap(({ templateId, deleteSurveyId }) =>
        this._templateService.getTemplateDetail(templateId).pipe(
          exhaustMap((detail) =>
            this._surveyService.createSurveyFromTemplate(
              detail,
              this._authSelectors.user().unwrap().id,
              this._authSelectors.activeCompanyId(),
            ),
          ),
          map((survey) => {
            this._toastService.success('Survey created from template', {
              keepOpen: false,
            });
            return SurveyCreationApiActions.createSurveyFromTemplateSuccess({
              survey,
              replacedSurveyId: deleteSurveyId,
            });
          }),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationApiActions.createSurveyFromTemplateFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  /**
   * Replacing the template on an existing survey deletes that survey — but only
   * once the replacement exists.
   *
   * Sequenced off create-success rather than off the command: the create is two
   * chained requests, so a command-triggered delete would land first and a
   * failing create would leave the buyer with neither survey. Legacy fired the
   * delete from a `tap` and accepted that window; this does not.
   *
   * A delete failure still cannot affect the create, since by this point the new
   * survey is already saved.
   *
   * `dispatch: false` — `createSurveyFromTemplate$` owns the outcome action.
   */
  public readonly deleteReplacedSurvey$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationApiActions.createSurveyFromTemplateSuccess),
        filter(
          (action): action is typeof action & { replacedSurveyId: number } =>
            !!action.replacedSurveyId,
        ),
        mergeMap(({ replacedSurveyId }) =>
          this._surveyService
            .deleteSurvey(replacedSurveyId)
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );
}
